import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Autocomplete } from '@material-ui/lab';
import {
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  TextField,
} from '@material-ui/core';
import { ArrowBack, ArrowForward } from '@material-ui/icons';
import { toast } from 'react-toastify';

import { producaov2 } from '../../../../../../services/apis';
import { getNodeToken } from '../../../../../../services/auth_node';
import { useDebounce } from '../../../../../../hooks/useDebounce';
import SignaturesModal from '../components/SignaturesModal';
import {
  FieldGroup,
  FieldRow,
  CheckboxGroup,
  SectionLabel,
  SignaturesButtonRow,
  SignatureChips,
  SignatureChip,
  SignatureEmptyMessage,
  SelectedCourseHint,
  ActionsRow,
} from './styles';

const MIN_SEARCH_LENGTH = 3;

// value: { title, id_curso, course_name, training_days, workload_hours,
//   emission_date, signatures (array de objetos completos), include_ementa,
//   is_english, hide_conclusion_date }
export default function CertificateStep({
  value,
  onChange,
  courseLabel,
  onCourseLabelChange,
  onBack,
  onNext,
  isSaving,
}) {
  const [courseSearch, setCourseSearch] = useState('');
  const [courseOptions, setCourseOptions] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [isLoadingCourses, setIsLoadingCourses] = useState(false);
  const [isSignaturesModalOpen, setIsSignaturesModalOpen] = useState(false);
  const debouncedCourseSearch = useDebounce(courseSearch);
  const courseReqSeq = useRef(0);

  const set = (patch) => onChange({ ...value, ...patch });

  const loadCourses = useCallback(async (search) => {
    if (!search || search.length < MIN_SEARCH_LENGTH) {
      setCourseOptions([]);
      return;
    }

    const seq = ++courseReqSeq.current;
    setIsLoadingCourses(true);
    try {
      const { data } = await producaov2.get('cursos-simplelist-new', {
        params: { search, limit: 15 },
        headers: { access_token: `voitto-token ${getNodeToken()}` },
      });
      if (seq !== courseReqSeq.current) return;
      setCourseOptions(Array.isArray(data) ? data : []);
    } catch (error) {
      if (seq !== courseReqSeq.current) return;
      toast.error('Não foi possível buscar os cursos.');
    } finally {
      if (seq === courseReqSeq.current) setIsLoadingCourses(false);
    }
  }, []);

  useEffect(() => {
    loadCourses(debouncedCourseSearch);
  }, [debouncedCourseSearch, loadCourses]);

  const onCourseSelected = (course) => {
    if (!course) return;
    setSelectedCourse(course);
    if (onCourseLabelChange) onCourseLabelChange(`${course.id} — ${course.curso}`);
    set({
      id_curso: course.id,
      course_name: value.course_name || course.curso,
    });
    setCourseSearch('');
    setCourseOptions([]);
  };

  const onSubmitSignatures = (signatures) => {
    set({ signatures });
    setIsSignaturesModalOpen(false);
  };

  const canNext =
    !!value.title &&
    !!value.id_curso &&
    !!value.course_name &&
    !!value.training_days &&
    !!value.workload_hours &&
    (value.signatures || []).length > 0;

  return (
    <div>
      <FieldGroup>
        <TextField
          fullWidth
          label="Título da turma"
          placeholder="C Vale — Green Belt — Abr–Jun/2026"
          value={value.title}
          onChange={(e) => set({ title: e.target.value })}
        />
      </FieldGroup>

      <FieldGroup>
        <Autocomplete
          options={courseOptions}
          loading={isLoadingCourses}
          value={null}
          inputValue={courseSearch}
          noOptionsText={
            courseSearch.length < MIN_SEARCH_LENGTH
              ? `Digite ao menos ${MIN_SEARCH_LENGTH} caracteres`
              : 'Nenhum curso encontrado'
          }
          getOptionLabel={(course) => (course ? `${course.id} — ${course.curso}` : '')}
          onInputChange={(event, newValue, reason) => {
            if (reason === 'input') setCourseSearch(newValue);
            if (reason === 'clear') setCourseSearch('');
          }}
          onChange={(event, course) => onCourseSelected(course)}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Curso base"
              placeholder="Digite o id ou nome do curso"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {isLoadingCourses && <CircularProgress size={16} />}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
        {value.id_curso && (
          <SelectedCourseHint>
            Curso selecionado:{' '}
            {selectedCourse
              ? `${selectedCourse.id} — ${selectedCourse.curso}`
              : courseLabel || `#${value.id_curso}`}
          </SelectedCourseHint>
        )}
      </FieldGroup>

      <FieldGroup>
        <TextField
          fullWidth
          label="Nome impresso no certificado"
          placeholder="Treinamento de Green Belt em Lean Seis Sigma"
          value={value.course_name}
          onChange={(e) => set({ course_name: e.target.value })}
        />
      </FieldGroup>

      <FieldRow>
        <TextField
          label="Dias de treinamento"
          placeholder="Ex: Abril a Junho de 2026"
          value={value.training_days}
          onChange={(e) => set({ training_days: e.target.value })}
        />
        <TextField
          label="Carga horária (horas)"
          placeholder="Ex: 40"
          type="number"
          value={value.workload_hours}
          onChange={(e) => set({ workload_hours: e.target.value })}
        />
        <TextField
          label="Data de emissão (opcional)"
          type="date"
          value={value.emission_date || ''}
          onChange={(e) => set({ emission_date: e.target.value || null })}
          InputLabelProps={{ shrink: true }}
        />
      </FieldRow>

      <CheckboxGroup>
        <FormControlLabel
          control={
            <Checkbox
              checked={value.include_ementa}
              onChange={(e) => set({ include_ementa: e.target.checked })}
            />
          }
          label="Incluir ementa (página 2)"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={value.is_english}
              onChange={(e) => set({ is_english: e.target.checked })}
            />
          }
          label="Gerar em inglês"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={value.hide_conclusion_date}
              onChange={(e) => set({ hide_conclusion_date: e.target.checked })}
            />
          }
          label="Ocultar data de conclusão"
        />
      </CheckboxGroup>

      <FieldGroup>
        <SectionLabel>Assinaturas</SectionLabel>
        <SignaturesButtonRow>
          <Button type="button" variant="outlined" onClick={() => setIsSignaturesModalOpen(true)}>
            Selecionar assinaturas
          </Button>
        </SignaturesButtonRow>
        <SignatureChips>
          {(value.signatures || []).length === 0 && (
            <SignatureEmptyMessage>Nenhuma assinatura selecionada</SignatureEmptyMessage>
          )}
          {(value.signatures || []).map((signature) => (
            <SignatureChip key={signature.id}>
              {signature.id} — {signature.title}
            </SignatureChip>
          ))}
        </SignatureChips>
        {isSignaturesModalOpen && (
          <SignaturesModal
            initialSelectedSignatures={value.signatures || []}
            onSubmit={onSubmitSignatures}
            onClose={() => setIsSignaturesModalOpen(false)}
          />
        )}
      </FieldGroup>

      <ActionsRow>
        <Button type="button" startIcon={<ArrowBack />} onClick={onBack} disabled={isSaving}>
          Voltar
        </Button>
        <Button
          type="button"
          variant="contained"
          color="primary"
          endIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : <ArrowForward />}
          disabled={!canNext || isSaving}
          onClick={onNext}
        >
          Avançar
        </Button>
      </ActionsRow>
    </div>
  );
}
